import { Link } from 'react-router-dom';

export default function SafetySection() {
  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100">
      <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
        {/* Left: Safety Checklist */}
        <div className="w-full lg:w-1/2">
          <span className="text-xs font-semibold text-accent-500 uppercase tracking-widest">
            Shopping Safety
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mt-3 mb-6 leading-tight">
            Safety Center &amp; Shopping Checklist
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-lg">
            Shopping smart means staying safe. Use this checklist every time you browse the Kakobuy Spreadsheet to avoid common pitfalls and ensure a smooth buying experience.
          </p>

          <div className="space-y-4">
            {[
              {
                risk: 'Fake or broken links',
                protection: 'Always verify sources and use links from the updated spreadsheet database',
                icon: 'ri-links-line',
              },
              {
                risk: 'Poor product quality',
                protection: 'Check QC photos provided in the spreadsheet before making a purchase decision',
                icon: 'ri-image-line',
              },
              {
                risk: 'Unexpected costs',
                protection: 'Calculate total price including shipping and fees before placing an order',
                icon: 'ri-money-dollar-circle-line',
              },
              {
                risk: 'Unknown sellers',
                protection: 'Research seller reputation and read community reviews for peace of mind',
                icon: 'ri-user-search-line',
              },
              {
                risk: 'Sizing issues',
                protection: 'Cross-reference size charts and measurement guides included in the database',
                icon: 'ri-ruler-line',
              },
            ].map((item) => (
              <div
                key={item.risk}
                className="flex items-start gap-4 bg-background-50 rounded-xl p-4 border border-background-200/60"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                    {item.risk}
                  </h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {item.protection}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Platform Comparison */}
        <div className="w-full lg:w-1/2">
          <span className="text-xs font-semibold text-primary-500 uppercase tracking-widest">
            Platform Comparison
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mt-3 mb-6 leading-tight">
            Spreadsheet Platform Comparison
          </h2>

          <div className="bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-background-200/60 bg-background-100/50">
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">Platform</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">Best For</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">Advantages</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">Beginner</th>
                </tr>
              </thead>
              <tbody className="text-xs md:text-sm">
                {[
                  {
                    name: 'Kakobuy Spreadsheet',
                    best: 'All Categories',
                    advantages: 'Most comprehensive, updated frequently, beginner guides included',
                    beginner: 'Yes',
                    highlight: true,
                  },
                  {
                    name: 'CSSBuy Spreadsheet',
                    best: 'Fashion & Sneakers',
                    advantages: 'Strong selection of streetwear finds, detailed QC photos',
                    beginner: 'Yes',
                    highlight: false,
                  },
                  {
                    name: 'Superbuy Spreadsheet',
                    best: 'General Shopping',
                    advantages: 'Wide variety of product types, established user community',
                    beginner: 'Moderate',
                    highlight: false,
                  },
                  {
                    name: 'Oopbuy Spreadsheet',
                    best: 'Budget Finds',
                    advantages: 'Affordable products, good for first-time exploration',
                    beginner: 'Yes',
                    highlight: false,
                  },
                ].map((row) => (
                  <tr
                    key={row.name}
                    className={`border-b border-background-200/40 hover:bg-background-100/30 transition-colors ${
                      row.highlight ? 'bg-accent-50/30' : ''
                    }`}
                  >
                    <td className="px-4 py-2.5 font-medium text-foreground-800 whitespace-nowrap">
                      {row.highlight && (
                        <i className="ri-star-fill text-accent-500 text-xs mr-1"></i>
                      )}
                      {row.name}
                    </td>
                    <td className="px-4 py-2.5 text-foreground-600 whitespace-nowrap">{row.best}</td>
                    <td className="px-4 py-2.5 text-foreground-600">{row.advantages}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.beginner === 'Yes'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}>
                        {row.beginner}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Guide CTA */}
          <div className="mt-6">
            <Link
              to="/kakobuy-spreadsheet-ultimate-guide-2026"
              className="inline-flex items-center gap-2 text-primary-500 text-sm font-semibold hover:text-primary-600 transition-colors duration-200"
            >
              Read the Complete Kakobuy Spreadsheet Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}